import React, { useCallback, useState } from "react";
import { useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSIconButton from "../../../system-design/DSIconButton";
import DSPaper from "../../../system-design/DSPaper";
import DSTooltip from "../../../system-design/DSTooltip";

type CollectionAssetFileInputUploadedProps = {
  secureUrl: string;
  fileName: string;
  downloadFileName: string;
  onRemoveFile: () => void;
  disabled?: boolean;
};

const overlayButtonSx = (spacing: (v: number) => string, hovered: boolean) => ({
  position: "absolute" as const,
  top: spacing(1),
  padding: spacing(1),
  opacity: hovered ? 1 : 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
  "&:hover": {
    transform: "scale(1.1)",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
  transition: "all 0.3s ease",
  zIndex: 2,
});

const CollectionAssetFileInputUploaded = ({
  secureUrl,
  fileName,
  downloadFileName,
  onRemoveFile,
  disabled = false,
}: CollectionAssetFileInputUploadedProps) => {
  const { spacing, palette } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(secureUrl);
      if (!response.ok)
        throw new Error(`Failed to download asset: ${response.statusText}`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName || downloadFileName;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Asset download error:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [secureUrl, fileName, downloadFileName]);

  return (
    <DSTooltip title={fileName} placement="top">
      <DSPaper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          display: "flex",
          aspectRatio: "1",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "stretch",
          borderRadius: spacing(3),
          border: "none",
          backgroundColor: palette.grey[200],
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        <img
          src={secureUrl}
          alt={fileName}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: isHovered ? 0.7 : 1,
            transition: "opacity 0.3s ease",
          }}
        />

        <DSIconButton
          onClick={handleDownload}
          disabled={isDownloading}
          title="Download"
          sx={{
            ...overlayButtonSx(spacing, isHovered),
            left: spacing(1),
            color: palette.common.white,
            "&:disabled": { color: palette.common.white },
          }}
        >
          {isDownloading ? (
            <DSCircularProgress size={24} color="inherit" />
          ) : (
            <DownloadIcon />
          )}
        </DSIconButton>

        {!disabled && (
          <DSIconButton
            onClick={onRemoveFile}
            sx={{
              ...overlayButtonSx(spacing, isHovered),
              right: spacing(1),
              color: palette.common.white,
            }}
          >
            <CloseIcon />
          </DSIconButton>
        )}
      </DSPaper>
    </DSTooltip>
  );
};

export default CollectionAssetFileInputUploaded;
